import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Save, Search, Ticket, X } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { suggestHandle } from '../lib/places'
import type { IngressoState, IngressoTheater, Place, Producer, SourcePlace } from '../types'
import { CollectionPanel } from './CollectionPanel'
import { LoadingState } from './LoadingState'

type Props = {
  canEdit: boolean
  catalogEditing: boolean
  places: Place[]
  producers: Producer[]
  // A place created here reaches every list of places.
  onCatalogChanged: () => Promise<void>
  onSaved: (message: string) => void
  onError: (message: string) => void
}

const normalize = (value: string | null | undefined) => String(value ?? '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

// The Guve place a source's cinema likely is: the same name in the same city.
const likelyPlace = (places: Place[], name: string, city: string | null) => places.find((place) =>
  normalize(place.name) === normalize(name) && (!city || normalize(place.city) === normalize(city)))?.id ?? ''

const placeLabel = (place: Place) => `${place.name}${place.city ? ` — ${place.city}` : ''}`

// One of a source's places, changed in place and saved on its own.
function SourceRow({ item, sourceName, places, producers, canEdit, onSave }: {
  item: SourcePlace
  sourceName: string
  places: Place[]
  producers: Producer[]
  canEdit: boolean
  onSave: (item: SourcePlace) => Promise<void>
}) {
  const [draft, setDraft] = useState(item)
  const [saving, setSaving] = useState(false)
  useEffect(() => setDraft(item), [item])
  const changed = draft.place_id !== item.place_id || draft.producer_id !== item.producer_id || draft.is_active !== item.is_active
  const save = async () => {
    setSaving(true)
    try { await onSave(draft) } finally { setSaving(false) }
  }
  return (
    <article className="source-row" aria-label={`${sourceName}: ${item.source_name}`}>
      <div className="source-row__main">
        <strong>{item.source_name}</strong>
        <small>{sourceName} · código {item.source_place_id}{item.source_city_id ? ` · cidade ${item.source_city_id}` : ''}{item.upcoming_sessions ? ` · ${item.upcoming_sessions} sessões por vir` : ''}</small>
      </div>
      <label className="field"><span>Lugar do Guve</span>
        <select aria-label={`Lugar do Guve: ${item.source_name}`} disabled={!canEdit} value={draft.place_id ?? ''} onChange={(event) => setDraft({ ...draft, place_id: event.target.value || null, is_active: event.target.value ? draft.is_active : false })}>
          <option value="">Sem lugar vinculado</option>
          {places.map((place) => <option key={place.id} value={place.id}>{placeLabel(place)}</option>)}
        </select>
      </label>
      <label className="field"><span>Perfil creditado</span>
        <select aria-label={`Perfil creditado: ${item.source_name}`} disabled={!canEdit} value={draft.producer_id ?? ''} onChange={(event) => setDraft({ ...draft, producer_id: event.target.value || null })}>
          <option value="">O perfil do lugar</option>
          {producers.map((producer) => <option key={producer.id} value={producer.id}>{producer.name}</option>)}
        </select>
      </label>
      <label className="source-row__toggle">
        <input type="checkbox" aria-label={`Ativo: ${item.source_name}`} disabled={!canEdit || !draft.place_id} checked={draft.is_active} onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })} />
        <span>{draft.is_active ? 'Ativo' : 'Inativo'}</span>
      </label>
      <span className="source-row__save">{canEdit && changed && <button type="button" className="button button--primary" disabled={saving} onClick={() => void save()}><Save size={15} /> {saving ? 'Salvando…' : 'Salvar'}</button>}</span>
    </article>
  )
}

// Ingresso.com's cinemas in a city, to add: each joins the Guve place it is,
// or creates that place with Ingresso.com's address and point.
function IngressoDialog({ existing, places, catalogEditing, onClose, onAdded, onError, onCatalogChanged }: {
  existing: Set<string>
  places: Place[]
  catalogEditing: boolean
  onClose: () => void
  onAdded: (message: string) => Promise<void>
  onError: (message: string) => void
  onCatalogChanged: () => Promise<void>
}) {
  const [states, setStates] = useState<IngressoState[]>([])
  const [uf, setUf] = useState('RJ')
  const [cityId, setCityId] = useState('')
  const [theaters, setTheaters] = useState<IngressoTheater[] | null>(null)
  const [choices, setChoices] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState('')
  // The latest error handler, without refetching when it changes.
  const report = useRef(onError)
  report.current = onError

  useEffect(() => {
    cmsApi.ingressoCities().then(setStates).catch((error) => report.current(error instanceof Error ? error.message : 'O Ingresso.com não respondeu.'))
  }, [])
  const cities = states.find((state) => state.uf === uf)?.cities ?? []
  const city = cities.find((item) => item.id === cityId)

  useEffect(() => {
    if (!cityId) { setTheaters(null); return }
    let current = true
    setTheaters(null)
    cmsApi.ingressoTheaters(cityId)
      .then((items) => {
        if (!current) return
        setTheaters(items)
        setChoices(Object.fromEntries(items.map((theater) => [theater.id, likelyPlace(places, theater.name, theater.city)])))
      })
      .catch((error) => { if (current) { setTheaters([]); report.current(error instanceof Error ? error.message : 'O Ingresso.com não respondeu.') } })
    return () => { current = false }
  }, [cityId, places])

  const add = async (theater: IngressoTheater, placeId: string) => {
    await cmsApi.saveSourcePlace({
      source: 'ingresso_com', source_place_id: theater.id, source_city_id: theater.city_id, source_name: theater.name,
      source_url: theater.url, time_zone: city?.time_zone ?? 'America/Sao_Paulo', place_id: placeId || null, is_active: Boolean(placeId),
    })
  }

  const link = async (theater: IngressoTheater) => {
    setBusy(theater.id)
    try {
      await add(theater, choices[theater.id] ?? '')
      await onAdded(choices[theater.id] ? `${theater.name} adicionado e ativo.` : `${theater.name} adicionado; vincule um lugar para ativar.`)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível adicionar o cinema.')
    } finally {
      setBusy('')
    }
  }

  // The place with Ingresso.com's data and its venue profile, then the cinema.
  const create = async (theater: IngressoTheater) => {
    setBusy(theater.id)
    try {
      const { place } = await cmsApi.savePlace({
        name: theater.name, address: theater.address, city: theater.city, state: theater.state,
        coordinates: theater.coordinates,
      }, { profile: { handle: suggestHandle(theater.name) } })
      await onCatalogChanged()
      await add(theater, place.id)
      await onAdded(`${theater.name} criado como lugar, com perfil, e ativo.`)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível criar o lugar.')
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation" onClick={() => !busy && onClose()}>
      <section className="dialog dialog--wide" role="dialog" aria-modal="true" aria-labelledby="ingresso-title" onClick={(event) => event.stopPropagation()}>
        <div className="dialog__heading">
          <div>
            <h2 id="ingresso-title">Cinemas do Ingresso.com</h2>
            <p>Escolha a cidade. Cada cinema entra ligado ao lugar do Guve que ele é; sem esse lugar, dá para criá-lo com o endereço e o ponto do Ingresso.com.</p>
          </div>
          <button type="button" className="icon-button" aria-label="Fechar" disabled={Boolean(busy)} onClick={onClose}><X size={18} /></button>
        </div>
        <div className="schedule-panel__grid">
          <label className="field"><span>Estado</span><select aria-label="Estado" value={uf} onChange={(event) => { setUf(event.target.value); setCityId('') }}>{states.map((state) => <option key={state.uf} value={state.uf}>{state.name}</option>)}</select></label>
          <label className="field"><span>Cidade</span><select aria-label="Cidade" value={cityId} onChange={(event) => setCityId(event.target.value)}><option value="">Escolha a cidade</option>{cities.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        </div>
        <div className="dialog__list">
          {!cityId ? <div className="empty-inline">Escolha uma cidade para ver os cinemas.</div>
            : theaters === null ? <LoadingState label="Buscando no Ingresso.com…" />
              : theaters.length ? theaters.map((theater) => {
                const added = existing.has(theater.id)
                return (
                  <article key={theater.id} className="theater-option" aria-label={theater.name}>
                    <div className="similar-event__main">
                      <strong>{theater.name}</strong>
                      <small>{[theater.address, theater.city, theater.state].filter(Boolean).join(' · ')} · código {theater.id}</small>
                    </div>
                    {added ? <span className="status-pill status-pill--published">Já cadastrado</span> : (
                      <div className="theater-option__actions">
                        <select aria-label={`Lugar do Guve: ${theater.name}`} value={choices[theater.id] ?? ''} onChange={(event) => setChoices((current) => ({ ...current, [theater.id]: event.target.value }))}>
                          <option value="">Sem lugar ainda</option>
                          {places.map((place) => <option key={place.id} value={place.id}>{placeLabel(place)}</option>)}
                        </select>
                        <button type="button" className="button button--primary" disabled={Boolean(busy)} onClick={() => void link(theater)}><Plus size={15} /> Adicionar</button>
                        {catalogEditing && !choices[theater.id] && (
                          <button type="button" className="button button--secondary" disabled={Boolean(busy)} onClick={() => void create(theater)}>Criar lugar e adicionar</button>
                        )}
                      </div>
                    )}
                  </article>
                )
              }) : <div className="empty-inline">Nenhum cinema do Ingresso.com nesta cidade.</div>}
        </div>
      </section>
    </div>
  )
}

// A VeloxTickets venue, typed as its page on the site names it.
function VeloxDialog({ places, onClose, onAdded, onError }: {
  places: Place[]
  onClose: () => void
  onAdded: (message: string) => Promise<void>
  onError: (message: string) => void
}) {
  const [fields, setFields] = useState({ code: '', name: '', city: '', url: '', place: '' })
  const [saving, setSaving] = useState(false)
  const set = (key: keyof typeof fields) => (event: { target: { value: string } }) => setFields((current) => ({ ...current, [key]: event.target.value }))
  const save = async () => {
    setSaving(true)
    try {
      await cmsApi.saveSourcePlace({
        source: 'veloxtickets', source_place_id: fields.code.trim(), source_name: fields.name.trim(),
        source_city_id: fields.city.trim() || null, source_url: fields.url.trim() || null,
        place_id: fields.place || null, is_active: Boolean(fields.place),
      })
      await onAdded(`${fields.name.trim()} adicionado${fields.place ? ' e ativo' : ''}.`)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível adicionar o local.')
      setSaving(false)
    }
  }
  return (
    <div className="dialog-backdrop" role="presentation" onClick={() => !saving && onClose()}>
      <section className="dialog" role="dialog" aria-modal="true" aria-labelledby="velox-title" onClick={(event) => event.stopPropagation()}>
        <div className="dialog__heading">
          <div><h2 id="velox-title">Local da VeloxTickets</h2><p>Como a página do local no site da VeloxTickets o mostra: o link termina com o código (…/Centro-de-Artes-UFF/<strong>BIL</strong>).</p></div>
          <button type="button" className="icon-button" aria-label="Fechar" disabled={saving} onClick={onClose}><X size={18} /></button>
        </div>
        <div className="form-grid">
          <label className="field"><span>Código *</span><input value={fields.code} onChange={set('code')} placeholder="BIL" /></label>
          <label className="field"><span>Nome *</span><input value={fields.name} onChange={set('name')} placeholder="Centro de Artes UFF" /></label>
          <label className="field"><span>Cidade no site</span><input value={fields.city} onChange={set('city')} placeholder="niteroi" /></label>
          <label className="field"><span>Link da página</span><input type="url" value={fields.url} onChange={set('url')} placeholder="https://www.veloxtickets.com/Portal/Local/…" /></label>
          <label className="field field--wide"><span>Lugar do Guve</span><select value={fields.place} onChange={set('place')}><option value="">Sem lugar ainda</option>{places.map((place) => <option key={place.id} value={place.id}>{placeLabel(place)}</option>)}</select></label>
        </div>
        <div className="dialog__actions">
          <button type="button" className="button button--secondary" disabled={saving} onClick={onClose}>Cancelar</button>
          <button type="button" className="button button--primary" disabled={saving || !fields.code.trim() || !fields.name.trim()} onClick={() => void save()}><Plus size={16} /> {saving ? 'Adicionando…' : 'Adicionar'}</button>
        </div>
      </section>
    </div>
  )
}

// The cinemas and theaters the automatic collection reads (docs/cms-hub.md):
// each feeds a Guve place and runs only while active. Until 14/10/2026 only a
// migration could add one.
export function SourcesPage({ canEdit, catalogEditing, places, producers, onCatalogChanged, onSaved, onError }: Props) {
  const [items, setItems] = useState<SourcePlace[]>([])
  const [sources, setSources] = useState<{ key: string; name: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState<'ingresso' | 'velox' | null>(null)

  const load = useCallback(async () => {
    setError('')
    try {
      const overview = await cmsApi.listSources()
      setItems(overview.places)
      setSources(overview.sources)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar as fontes.')
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  const sourceName = (key: string) => sources.find((source) => source.key === key)?.name ?? key
  const shown = useMemo(() => items.filter((item) => (filter === 'all' || item.source === filter)
    && (!search.trim() || normalize(item.source_name).includes(normalize(search)) || normalize(item.place_name).includes(normalize(search)))), [items, filter, search])
  const unlinked = items.filter((item) => !item.place_id).length

  const save = async (item: SourcePlace) => {
    try {
      await cmsApi.saveSourcePlace(item)
      onSaved(`${item.source_name} salvo.`)
      await load()
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível salvar.')
    }
  }
  const added = async (message: string) => {
    setDialog(null)
    onSaved(message)
    await load()
  }

  return (
    <div className="page-stack">
      <section className="page-heading page-heading--with-action">
        <div>
          <h1>Fontes</h1>
          <p>Os cinemas e teatros que a coleta lê. Cada um alimenta um lugar do Guve e só é lido enquanto está ativo.</p>
        </div>
        {canEdit && <div className="page-heading__actions">
          <button className="button button--secondary" onClick={() => setDialog('velox')}><Plus size={17} /> Local da VeloxTickets</button>
          <button className="button button--primary" onClick={() => setDialog('ingresso')}><Search size={17} /> Buscar no Ingresso.com</button>
        </div>}
      </section>

      <CollectionPanel canEdit={canEdit} onSaved={onSaved} onError={onError} />

      <section className="panel panel--flush">
        <div className="filter-bar filter-bar--sources">
          <label className="search-field"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por nome" /></label>
          <select aria-label="Fonte" value={filter} onChange={(event) => setFilter(event.target.value)}>
            <option value="all">Todas as fontes</option>
            {sources.map((source) => <option key={source.key} value={source.key}>{source.name}</option>)}
          </select>
        </div>
        {loading ? <LoadingState label="Carregando fontes…" /> : error ? (
          <div className="error-state"><p>{error}</p><button className="button button--secondary" onClick={() => void load()}>Tentar novamente</button></div>
        ) : shown.length ? (
          <div className="source-list">{shown.map((item) => (
            <SourceRow key={`${item.source}:${item.source_place_id}`} item={item} sourceName={sourceName(item.source)} places={places} producers={producers} canEdit={canEdit} onSave={save} />
          ))}</div>
        ) : <div className="empty-state"><Ticket size={30} /><h3>Nenhum local</h3><p>Busque cinemas no Ingresso.com ou adicione um local da VeloxTickets.</p></div>}
        {!loading && !error && <div className="pagination"><span>{items.length} {items.length === 1 ? 'local' : 'locais'}{unlinked ? ` · ${unlinked} sem lugar do Guve` : ''}</span></div>}
      </section>

      {dialog === 'ingresso' && (
        <IngressoDialog
          existing={new Set(items.filter((item) => item.source === 'ingresso_com').map((item) => item.source_place_id))}
          places={places}
          catalogEditing={catalogEditing}
          onClose={() => setDialog(null)}
          onAdded={added}
          onError={onError}
          onCatalogChanged={onCatalogChanged}
        />
      )}
      {dialog === 'velox' && <VeloxDialog places={places} onClose={() => setDialog(null)} onAdded={added} onError={onError} />}
    </div>
  )
}
